<template>
  <div class="account-info-section">
    <b-card class="shadow" no-body>
      <b-card-body>
        <b-card-title class="dark-variant">Account</b-card-title>
        <div class="account-info">
          <b-card-text>
            <div class="account-status">
              <label for="status" class="dark-variant">Account Status</label>
              <span class="text-success">Active</span>
            </div>
            <div class="account-type">
              <label for="text" class="dark-variant">Account type</label>
              <span class="text-primary">{{ accountType }}</span>
            </div>
          </b-card-text>
        </div>
      </b-card-body>
    </b-card>

    <!-- ============================ DELETE ACCOUNT ============================ -->
    <b-card class="shadow" no-body>
      <b-card-body>
        <b-card-title class="dark-variant">Delete Account</b-card-title>
        <div>
          <b-alert v-if="deleteAccountSuccess" show variant="success">
            Your account has been deleted. Signing you out...
          </b-alert>
        </div>
        <div class="credit-card-info">
          <p class="deactivate-notice">
            Deleting your account closes your Amejpay wallet permanently and removes your
            profile, KYC records and transaction history from the app.
            <br />This cannot be undone, and the same email cannot be reused to sign in again.
          </p>
          <p class="deactivate-warning">
            <i class="material-icons">info</i>
            <span>
              Withdraw any remaining balance first &mdash; funds left in the wallet cannot be
              recovered once the account is gone.
            </span>
          </p>
        </div>
        <div class="button">
          <b-button variant="light" class="danger-action" v-b-modal.delete-account>
            Delete Account
          </b-button>
        </div>
      </b-card-body>
    </b-card>

    <b-modal
      id="delete-account"
      size="md"
      title="Delete Account"
      v-bind:hide-footer="true"
      @show="resetDeleteForm"
    >
      <b-form @submit.stop.prevent="confirmDeleteAccount">
        <p><strong>Are you sure you want to delete your account?</strong></p>
        <p>
          This is permanent. Your wallet is closed and your profile, KYC records and
          transaction history are removed. You will not be able to sign in again.
        </p>

        <b-alert variant="danger" :show="Boolean(formError)">{{ formError }}</b-alert>

        <label for="delete-account-password" class="dark-variant">
          Enter your password to confirm *
        </label>
        <b-form-input
          id="delete-account-password"
          v-model="password"
          type="password"
          placeholder="Your account password"
          autocomplete="current-password"
          :class="errors.password ? 'errors' : ''"
        ></b-form-input>
        <span class="text-danger" v-if="errors.password">{{ errors.password }}</span>

        <hr />
        <div class="text-right">
          <b-button
            variant="secondary"
            @click="$bvModal.hide('delete-account')"
            class="mr-1"
            :disabled="isDeleting"
          >Cancel</b-button>
          <b-button variant="danger" type="submit" class="ml-1" :disabled="isDeleting">
            <span v-if="isDeleting">Deleting...</span>
            <span v-else>Delete My Account</span>
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- ======================= REQUEST FOR MERCHANT ACCOUNT ======================= -->
    <b-card class="shadow" no-body>
      <b-card-body>
        <b-card-title class="dark-variant">Request for Merchant Account</b-card-title>
        <div class="credit-card-info">
          <p class="deactivate-notice">
            A merchant account lets you accept payments from customers, use the Amejpay
            payment gateway with live and sandbox API keys, and settle your earnings to a
            business bank account.
            <br />You will be asked for your business details and supporting documents; our
            team reviews each application before the account is enabled.
          </p>
        </div>
        <div class="button">
          <router-link :to="{ name: 'Merchant' }">
            <b-button variant="light">Submit a Request</b-button>
          </router-link>
        </div>
      </b-card-body>
    </b-card>

    <b-toast
      id="account-error-toast"
      variant="danger"
      solid
      append-toast
      toaster="b-toaster-top-center"
    >
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Error!!!</strong>
      </div>
      <p>{{ formError }}</p>
    </b-toast>
  </div>
</template>
<script>
import { mapState } from "vuex";
export default {
  name: "AccountInfo",
  data() {
    return {
      password: "",
      errors: {},
      formError: "",
      isDeleting: false,
      deleteAccountSuccess: false,
    };
  },
  computed: {
    ...mapState(["profile", "successmessage", "errormessage", "message"]),
    /** Merchant accounts were previously mislabelled: this was hard-coded "Personal Account". */
    accountType() {
      const p = this.profile || {};
      const author = p.author || {};
      return (p.merchant || author.merchant) ? "Merchant Account" : "Personal Account";
    },
  },
  methods: {
    resetDeleteForm() {
      this.password = "";
      this.errors = {};
      this.formError = "";
      this.isDeleting = false;
    },

    /**
     * DELETE /v1/account/delete with the password in the body.
     * The server is what actually validates the password - this only stops accidents.
     */
    confirmDeleteAccount() {
      if (this.isDeleting) { return; }
      this.errors = {};
      this.formError = "";

      if (!this.password) {
        this.errors.password = "Your password is required to delete the account.";
        return;
      }

      const vm = this;
      this.isDeleting = true;

      this.$store
        .dispatch("deleteAccount", { password: this.password })
        .then(function (res) {
          if (res && res.ok) {
            vm.$bvModal.hide("delete-account");
            vm.deleteAccountSuccess = true;
            vm.password = "";
            // The token belongs to an account that no longer exists - drop it and leave.
            setTimeout(function () {
              window.localStorage.removeItem("token");
              window.sessionStorage.removeItem("token");
              window.location.replace("/logout");
            }, 1800);
          } else {
            vm.isDeleting = false;
            vm.formError = (res && res.message) || "Unable to delete the account.";
            vm.$bvToast.show("account-error-toast");
          }
        })
        .catch(function () {
          vm.isDeleting = false;
          vm.formError = "Unable to delete the account. Please try again.";
          vm.$bvToast.show("account-error-toast");
        });
    },
  },
};
</script>
<style lang="scss">
  @import '../../css/style.scss';
</style>
<style type="text/css" scoped>
.account-info-section .deactivate-warning{
  display: flex;
  align-items: flex-start;
  background: #fff8e6;
  border: 1px solid #ffe2a8;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #7a5b12;
  margin-top: 10px;
}
.account-info-section .deactivate-warning > i{
  font-size: 16px;
  margin-right: 8px;
  flex: 0 0 auto;
}
.account-info-section .danger-action{
  color: #dc3545;
  border-color: #f0b6bc;
}
.account-info-section .danger-action:hover{
  background: #dc3545;
  border-color: #dc3545;
  color: #fff;
}
</style>
